// ChangeStatus Configuration - UNIFIED APPROACH
// Manages change request statuses (Draft, Submitted, Approved, Implemented, etc.)
// Based on Prisma ChangeStatus model schema

import { createTrackingConfig } from '../../../components/generic/labels';
import { tableConfig, uiConfig } from '../../../config/appConfig';

export const changeStatusConfig = {
  entityName: 'ChangeStatus',
  
  api: {
    baseEndpoint: '/change-status',
    endpoints: {
      list: '', create: '', update: (id) => `/${id}`, delete: (id) => `/${id}`, getById: (id) => `/${id}`,
      activate: (id) => `/${id}/activate`, deactivate: (id) => `/${id}/deactivate`, reorder: '/reorder'
    },
    lookupEndpoints: {}
  },
  
  ui: {
    drawerWidth: '600px', defaultPageSize: tableConfig.defaultPageSize,
    pageSizeOptions: tableConfig.pageSizeOptions, defaultTableDensity: tableConfig.defaultDensity,
    requiredFieldStyle: uiConfig.requiredFieldStyle
  },
  
  service: {
    name: 'changeStatusService',
    methods: {
      getAll: 'getAllChangeStatuses', getById: 'getChangeStatusById', 
      create: 'createChangeStatus', update: 'updateChangeStatus',
      delete: 'deleteChangeStatus', validate: 'validateData'
    }
  },

  fields: [
    { name: 'id', label: 'ID', type: 'hidden', table: false, filterable: false },
    { 
      name: 'name', label: 'Status Name', type: 'text', required: true,
      placeholder: 'Enter change status name (e.g., Draft, Submitted, Approved)',
      table: { header: 'Status Name', width: '200px', sortable: true }, filterable: true,
      validation: { required: true, minLength: 2, maxLength: 100, pattern: /^[A-Za-z\s&\-'.()]+$/ }
    },
    { 
      name: 'description', label: 'Description', type: 'textarea', required: false,
      placeholder: 'Enter a description for this change status (optional)',
      table: { header: 'Description', width: '300px', sortable: false }, filterable: true,
      validation: { required: false, maxLength: 500 },
      rows: 3
    },
    { 
      name: 'isActive', label: 'Active', type: 'switch', required: false, defaultValue: true,
      table: { header: 'Status', width: '100px', sortable: true, cellRenderer: 'boolean' },
      filterable: true, validation: { required: false },
      switchLabel: 'Status is active and available for use'
    },
    { 
      name: 'record_order', label: 'Display Order', type: 'number', required: false,
      placeholder: 'Enter display order (optional)',
      table: { header: 'Order', width: '80px', sortable: true }, filterable: false,
      validation: { required: false, min: 1, max: 999, integer: true },
      step: 1, decimals: 0, allowNegative: false
    },
    { 
      name: 'color', label: 'Display Color', type: 'color', required: false,
      table: { header: 'Color', width: '100px', sortable: false, cellRenderer: 'color' },
      validation: { required: false, pattern: /^(#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})|[a-z]+)$/ }
    }
  ],

  get tableColumns() {
    return this.fields.filter(field => field.table !== false && !field.hidden)
      .map(field => ({ key: field.name, header: field.table?.header || field.label,
        width: field.table?.width || 'auto', sortable: field.table?.sortable !== false,
        displayField: field.table?.displayField || field.name,
        cellRenderer: field.table?.cellRenderer || 'default' }));
  },

  filterableFields: ['name', 'description', 'isActive'],
  filterGroups: [
    { name: 'basic', label: 'Basic Filters', fields: ['name', 'description', 'isActive'] },
    { name: 'advanced', label: 'Advanced Filters', fields: ['record_order'] }
  ],

  customSortOrders: { isActive: [true, false], record_order: 'asc' },

  messages: {
    loading: 'Loading change statuses...', noData: 'No change statuses found. Click "Add Change Status" to create the first status.',
    loadError: 'Failed to load change statuses', saveSuccess: 'Change status saved successfully!',
    saveError: 'Failed to save change status', deleteSuccess: 'Change status deleted successfully!',
    deleteError: 'Failed to delete change status',
    deleteConfirm: 'Are you sure you want to delete this change status? This action cannot be undone and may affect change requests that reference this status.'
  },

  layout: {
    mode: 'sections',
    sections: [{
      id: 'basic', title: 'Change Status Information', icon: '🏷️', columns: 2,
      fields: [{ name: 'name' }, { name: 'description' }, { name: 'isActive' }, { name: 'record_order' }, { name: 'color' }]
    }]
  },

  trackingSection: createTrackingConfig('ChangeStatus'),

  labels: {
    singular: 'Change Status', plural: 'Change Statuses', addButton: 'Add Change Status',
    editButton: 'Edit Change Status', deleteButton: 'Delete Change Status',
    pageTitle: 'Change Status Management', pageSubtitle: 'Manage change request statuses for tracking progress',
    searchPlaceholder: 'Search change statuses by name or description...'
  },

  fieldsToRemove: ['id', 'createdAt', 'updatedAt', 'createdById', 'updatedById', 'changeRequests'],
  exportFileName: 'change-statuses',

  transformData: (data) => {
    const transformed = { ...data };
    if (transformed.record_order !== undefined) {
      if (transformed.record_order === '' || transformed.record_order === null) delete transformed.record_order;
      else { const numValue = parseInt(transformed.record_order, 10); if (!isNaN(numValue)) transformed.record_order = numValue; else delete transformed.record_order; }
    }
    return transformed;
  },

  validateField: null
};
